import { CalendarRange, FileText, LoaderCircle } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { descargarLotosPDF, type FiltrosExport, mensajeDeErrorExport } from "@/lib/export";
import { MENSAJE_REQUIERE_CONEXION } from "@/lib/offline-policy";
import { useOnlineStatus } from "@/lib/online-status";

type ExportLotosProps = {
  obraId: number;
  /**
   * Rango de apertura de los filtros de la página (#247), en YYYY-MM-DD. Siembra el
   * popover para que el libro exportado arranque acotado a LO QUE SE ESTÁ VIENDO; desde ahí
   * se puede seguir afinando sin tocar la lista.
   */
  desdeInicial?: string;
  hastaInicial?: string;
  /**
   * filtrosURL son TODOS los filtros activos del índice (#247) y viajan ENTEROS en la
   * descarga: desde #295 el servidor los aplica, así que el libro (y el PDF) traen
   * exactamente lo que se está viendo. Es también la FUENTE del rango del listado con el
   * que se interseca el popover (#307): lo que la pantalla muestra es lo que dicen SUS
   * filtros, no la semilla del popover.
   */
  filtrosURL?: FiltrosExport;
};

/**
 * masTarde y masTemprano intersecan un extremo del rango del listado con el del popover
 * (#307, D2): `desde` se queda con el MÁS TARDÍO de los dos y `hasta` con el MÁS TEMPRANO,
 * y un extremo vacío no significa «sin límite» sino que HEREDA el del otro lado. Así la
 * descarga nunca puede traer más filas que la pantalla: acotar más siempre se permite,
 * ampliar no. Las fechas son YYYY-MM-DD, donde el orden alfabético ES el cronológico.
 */
function masTarde(a?: string, b?: string): string | undefined {
  if (!a) {
    return b || undefined;
  }
  if (!b) {
    return a;
  }
  return a > b ? a : b;
}

function masTemprano(a?: string, b?: string): string | undefined {
  if (!a) {
    return b || undefined;
  }
  if (!b) {
    return a;
  }
  return a < b ? a : b;
}

/**
 * MENSAJE_RANGO_VACIO explica por qué no hay nada que descargar cuando la intersección del
 * rango del listado con el del popover se queda sin días (#307, D5). Se dice en el popover y
 * también en el `title` de los botones apagados, para quien lo cerró sin corregirlo.
 */
const MENSAJE_RANGO_VACIO =
  "El rango elegido no se cruza con el del listado: no hay ningún día que exportar.";

/**
 * ExportLotos agrupa la exportación xlsx de LOTOs (#214) con sus filtros: arrastra TODOS los
 * de la URL (#295) y añade un rango opcional de fechas de apertura en un pequeño popover
 * junto al botón de exportar. Los dos inputs de fecha se apilan (mobile-first); el popover se
 * cierra al pulsar fuera o con Escape.
 *
 * Desde #249 (D4) TODOS los listados exportan con sus filtros, y desde #295 los LOTOs también:
 * lo que los distingue es solo el popover de rango, que afina la descarga sin tocar la lista.
 * Afinar, nunca ampliar (#307): el rango que se pide es la intersección de los dos, así que el
 * libro jamás trae filas que la pantalla no esté mostrando.
 */
export function ExportLotos({
  obraId,
  desdeInicial = "",
  hastaInicial = "",
  filtrosURL,
}: ExportLotosProps) {
  const [abierto, setAbierto] = useState(false);
  const [desde, setDesde] = useState(desdeInicial);
  const [hasta, setHasta] = useState(hastaInicial);

  // Si la página cambia su rango de fechas, el popover se resincroniza: la exportación
  // sigue por defecto a lo que se está viendo.
  useEffect(() => {
    setDesde(desdeInicial);
  }, [desdeInicial]);
  useEffect(() => {
    setHasta(hastaInicial);
  }, [hastaInicial]);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const desdeId = useId();
  const hastaId = useId();

  // Cierra el popover al pulsar fuera o con Escape mientras está abierto.
  useEffect(() => {
    if (!abierto) {
      return;
    }
    function onPointerDown(e: MouseEvent) {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setAbierto(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [abierto]);

  const hayRango = desde !== "" || hasta !== "";
  // El rango de apertura que se pide es la INTERSECCIÓN del que trae el listado con el del
  // popover (#307, D1/D2): vaciar un extremo aquí ya no borra el límite de la pantalla, lo
  // hereda. La descarga solo puede afinar lo que se está viendo, nunca ampliarlo.
  const desdeExport = masTarde(filtrosURL?.desde, desde);
  const hastaExport = masTemprano(filtrosURL?.hasta, hasta);
  // Si la intersección no deja NINGÚN día, la descarga no se pide (#307, D5): el servidor
  // respondería un 400 FILTRO_INVALIDO y el botón un «No se pudo exportar» que no explica nada.
  const rangoVacio =
    desdeExport !== undefined && hastaExport !== undefined && desdeExport > hastaExport;
  // Los filtros de la URL viajan ENTEROS y son la única fuente (#319, D3): el estado llega por
  // ahí como cualquier otro, así que ninguno se pierde por el camino —perderlo ensancharía la
  // descarga, la misma fuga que cerró #307—. Solo el rango se recalcula, porque es lo único que
  // el popover puede afinar. Un valor `undefined` no se serializa (ver queryDeFiltros).
  const filtros: FiltrosExport = {
    ...filtrosURL,
    desde: desdeExport,
    hasta: hastaExport,
  };

  return (
    // El contenedor externo apila los botones y es el que vigila el clic fuera. El popover NO
    // cuelga de él sino de la FILA de botones (`relative` propio).
    <div ref={contenedorRef} className="flex flex-col items-end gap-1">
      <div className="relative flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-label="Rango de fechas de exportación"
          aria-expanded={abierto}
          onClick={() => setAbierto((v) => !v)}
          className={hayRango ? "border-primary-500 text-primary-700" : undefined}
        >
          <CalendarRange className="h-5 w-5" aria-hidden="true" />
        </Button>
        <ExportButton
          tipo="lotos"
          obraId={obraId}
          filtros={filtros}
          motivoDeshabilitado={rangoVacio ? MENSAJE_RANGO_VACIO : undefined}
        />
        <ExportLotosPDFButton
          obraId={obraId}
          filtros={filtros}
          motivoDeshabilitado={rangoVacio ? MENSAJE_RANGO_VACIO : undefined}
        />

        {abierto ? (
          <div className="absolute top-full right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
            <p className="font-semibold text-slate-900 text-sm">Rango de apertura</p>
            {/* La copia dice lo que el componente hace de verdad (#307): el rango solo afina
                la descarga; para llevarse más filas hay que ampliar antes el listado. */}
            <p className="mb-3 text-slate-500 text-xs">
              El export se acota siempre al listado que estás viendo: aquí solo puedes afinarlo más.
            </p>
            <div className="flex flex-col gap-3">
              {/* Los topes de cada campo se calculan contra el rango EFECTIVO, no contra el
                  estado del popover: si no, vaciar «Desde» soltaba el `min` de «Hasta» y se
                  podía elegir un día anterior al `desde` heredado del listado (#307). */}
              <div className="flex flex-col gap-1">
                <Label htmlFor={desdeId}>Desde</Label>
                <Input
                  id={desdeId}
                  type="date"
                  value={desde}
                  max={hastaExport}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={hastaId}>Hasta</Label>
                <Input
                  id={hastaId}
                  type="date"
                  value={hasta}
                  min={desdeExport}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </div>
              {rangoVacio ? (
                <p role="alert" className="text-red-600 text-xs">
                  {MENSAJE_RANGO_VACIO}
                </p>
              ) : null}
              {hayRango ? (
                <button
                  type="button"
                  onClick={() => {
                    setDesde("");
                    setHasta("");
                  }}
                  className="self-start text-primary-700 text-sm hover:underline"
                >
                  Limpiar rango
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * ExportLotosPDFButton descarga el resumen tabular en PDF de los LOTOs (#238) con los mismos
 * filtros que el botón de Excel — los nueve del índice desde #295 (D2). Mantiene su propio
 * estado de carga/fallo y muestra el error inline debajo, igual que ExportButton, para no
 * depender de un sistema de toasts; qué DICE ese error lo decide `mensajeDeErrorExport` (#355),
 * la misma regla que sus dos hermanos —importa aquí porque el tope del resumen PDF es OTRO
 * (5.000 filas, #323) y un texto propio podría nombrar el del xlsx—. `motivoDeshabilitado` lo
 * apaga por una razón del llamante (#307: un rango que no se cruza con el del listado), igual
 * que en ExportButton.
 *
 * Descargar el PDF también lo construye el SERVIDOR, así que lleva el MISMO gate de conectividad
 * que su hermano de Excel (#319, D1): sin red confirmada se deshabilita con «Requiere conexión»
 * en vez de dejarse pulsar y morir en un «No se pudo exportar» que culpa al sistema de lo que
 * solo es falta de red. El arranque es fail-closed (#237): mientras el heartbeat no confirma, la
 * conectividad cuenta como caída. Y la conectividad MANDA sobre `motivoDeshabilitado` (D2): es la
 * causa que el usuario puede resolver primero.
 */
function ExportLotosPDFButton({
  obraId,
  filtros,
  motivoDeshabilitado,
}: {
  obraId: number;
  filtros: FiltrosExport;
  motivoDeshabilitado?: string;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const online = useOnlineStatus();

  async function onExport() {
    setPending(true);
    setError(null);
    try {
      await descargarLotosPDF(obraId, filtros);
    } catch (err) {
      setError(mensajeDeErrorExport(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col items-stretch gap-1">
      <Button
        variant="outline"
        onClick={() => void onExport()}
        disabled={pending || !online || motivoDeshabilitado !== undefined}
        title={online ? motivoDeshabilitado : MENSAJE_REQUIERE_CONEXION}
        aria-label="Exportar a PDF"
      >
        {pending ? (
          <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <FileText className="h-5 w-5" aria-hidden="true" />
        )}
        <span className="hidden sm:inline">PDF</span>
      </Button>
      {error !== null ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </div>
  );
}
